import moment from 'moment';
import EventBus from 'eventing-bus';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { History as HistoryIcon, Compare as CompareIcon, Restore as RestoreIcon, Visibility as ViewIcon, MoreVert as MoreIcon, ArrowBack as BackIcon, Save as SaveIcon, Public as PublishIcon, Lock as DraftIcon } from '@mui/icons-material';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, CircularProgress } from '@mui/material';

import { getWorkflowVersions, createWorkflowVersion, restoreWorkflowVersion } from '../../store/Workflows';

const WorkflowVersions = () => {
  const { workflowId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [createVersionDialog, setCreateVersionDialog] = useState(false);
  const [compareDialog, setCompareDialog] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuVersion, setMenuVersion] = useState(null);
  const [versionData, setVersionData] = useState({ message: '', tag: '' });
  const { workflowVersions, versionsLoading, singleWorkflow } = useSelector(state => state.Workflows);
  
  useEffect(() => {
    if (workflowId) dispatch(getWorkflowVersions(workflowId));
  }, [workflowId]);
  
  const handleCreateVersion = () => setCreateVersionDialog(true);
  
  const handleSaveVersion = () => {
    dispatch(createWorkflowVersion({
      workflowId,
      data: {
        message: versionData.message,
        tag: versionData.tag,
        workflow: singleWorkflow
      },
      successCallback: () => {
        setCreateVersionDialog(false);
        setVersionData({ message: '', tag: '' });
        EventBus.publish('success', 'Version created successfully');
        dispatch(getWorkflowVersions(workflowId));
      },
      failCallback: (error) => { EventBus.publish('error', 'Failed to create version') }
    }));
  };
  
  const handleRestoreVersion = (version) => {
    setVersionToRestore(version);
    setRestoreDialog(true);
  };
  
  const confirmRestore = () => {
    if (versionToRestore) {
      dispatch(restoreWorkflowVersion({
        workflowId,
        versionId: versionToRestore._id,
        successCallback: () => {
          setRestoreDialog(false);
          setVersionToRestore(null);
          EventBus.publish('success', 'Version restored successfully');
          history.push(`/agent/workflows/${workflowId}/edit`);
        },
        failCallback: (error) => {
          EventBus.publish('error', 'Failed to restore version');
        }
      }));
    }
  };
  
  const handleCompareVersions = () => {
    if (selectedVersions.length === 2) {
      setCompareDialog(true);
    } else {
      EventBus.publish('error', 'Please select exactly 2 versions to compare');
    }
  };
  
  const handleViewVersion = (version) => {
    // Navigate to version detail view
    history.push(`/agent/workflows/${workflowId}/versions/${version._id}`);
  };
  
  const handleMenuOpen = (event, version) => {
    setAnchorEl(event.currentTarget);
    setMenuVersion(version);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuVersion(null);
  };
  
  const isVersionSelected = (versionId) => {
    return selectedVersions.some(v => v._id === versionId);
  };
  
  const toggleVersionSelection = (version) => {
    if (isVersionSelected(version._id)) {
      setSelectedVersions(selectedVersions.filter(v => v._id !== version._id));
    } else {
      if (selectedVersions.length < 2) {
        setSelectedVersions([...selectedVersions, version]);
      } else {
        EventBus.publish('warning', 'You can only compare 2 versions at a time');
      }
    }
  };
  
  if (versionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => history.goBack()}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5">
            Workflow Version History
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedVersions.length === 2 && (
            <Button
              variant="outlined"
              startIcon={<CompareIcon />}
              onClick={handleCompareVersions}
            >
              Compare Selected
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleCreateVersion}
          >
            Create Version
          </Button>
        </Box>
      </Box>
      
      {/* Version Information */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Current Version
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Version: {singleWorkflow?.version || '1.0.0'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last Modified: {moment(singleWorkflow?.updatedAt).format('MMM DD, YYYY HH:mm')}
          </Typography>
          <Chip
            label={singleWorkflow?.status}
            size="small"
            color={singleWorkflow?.status === 'active' ? 'success' : 'default'}
          />
        </Box>
      </Paper>
      
      {/* Version History Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" width="50">
                {/* Checkbox header */}
              </TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Tag</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workflowVersions?.map((version, index) => (
              <TableRow key={version._id} hover>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={isVersionSelected(version._id)}
                    onChange={() => toggleVersionSelection(version)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    v{version.versionNumber}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {version.message || 'No message'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {version.tag && (
                    <Chip label={version.tag} size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {version.createdBy?.name || 'System'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {moment(version.createdAt).format('MMM DD, YYYY HH:mm')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={version.isPublished ? <PublishIcon /> : <DraftIcon />}
                    label={version.isPublished ? 'Published' : 'Draft'}
                    size="small"
                    color={version.isPublished ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="View Version">
                      <IconButton
                        size="small"
                        onClick={() => handleViewVersion(version)}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Restore Version">
                      <IconButton
                        size="small"
                        onClick={() => handleRestoreVersion(version)}
                        disabled={index === 0} // Can't restore current version
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, version)}
                    >
                      <MoreIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Create Version Dialog */}
      <Dialog open={createVersionDialog} onClose={() => setCreateVersionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Version</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Version Message"
              value={versionData.message}
              onChange={(e) => setVersionData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Describe the changes in this version..."
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Tag (optional)"
              value={versionData.tag}
              onChange={(e) => setVersionData(prev => ({ ...prev, tag: e.target.value }))}
              placeholder="e.g., v2.0, stable, beta"
              helperText="Add a tag to easily identify this version"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateVersionDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveVersion}
            disabled={!versionData.message.trim()}
          >
            Create Version
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialog} onClose={() => setRestoreDialog(false)}>
        <DialogTitle>Restore Version</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Are you sure you want to restore version {versionToRestore?.versionNumber}? 
            This will replace the current workflow with the selected version.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={confirmRestore}
          >
            Restore
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Version Comparison Dialog */}
      <Dialog open={compareDialog} onClose={() => setCompareDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareIcon />
            Compare Versions
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedVersions.length === 2 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Comparing v{selectedVersions[0].versionNumber} with v{selectedVersions[1].versionNumber}
              </Typography>
              {/* Add version comparison UI here */}
              <Alert severity="info">
                Version comparison feature will show differences in nodes, edges, and settings between the selected versions.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Version Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          // Implement download version
          handleMenuClose();
        }}>
          <ListItemIcon>
            <SaveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download as JSON</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          // Implement publish version
          handleMenuClose();
        }}>
          <ListItemIcon>
            <PublishIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Publish Version</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default WorkflowVersions;